import { Check, Loader2, Palette, RotateCcw } from 'lucide-react'
import type { ReactNode } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { FormField } from '@/components/FormField'
import { cn } from '@/lib/cn'
import type { ColorPreset } from '@/features/branding/extras'

const HEX_RE = /^#[0-9a-fA-F]{6}$/

/**
 * Generic "pick a colour" card used by the secondary theme dimensions
 * (card surface, text colour, sidebar text, secondary button). The
 * brand-colour and surface-tone cards stay inline in BrandingPage
 * because they each have a custom live-preview block; everything else
 * follows the same swatches + hex + native-picker pattern this
 * component implements.
 */
export interface ColorPickerSectionProps {
  title: string
  description: string
  presets: ColorPreset[]
  /** The hex saved on the tenant, or null when "use default". */
  saved: string | null
  /** The hex the user has selected in the UI but not yet saved. */
  draft: string
  /** What's currently in the typed hex input - may be invalid mid-edit. */
  hexInput: string
  /** Default to fall back to when reset / the user clears their pick. */
  defaultHex: string
  canEdit: boolean
  busy: boolean
  onPick: (hex: string) => void
  onHexChange: (next: string) => void
  onSave: () => void
  onReset: () => void
  /** Optional preview rendered below the inputs. */
  preview?: ReactNode
}

export function ColorPickerSection({
  title,
  description,
  presets,
  saved,
  draft,
  hexInput,
  defaultHex,
  canEdit,
  busy,
  onPick,
  onHexChange,
  onSave,
  onReset,
  preview,
}: ColorPickerSectionProps) {
  const isValidHex = HEX_RE.test(hexInput.trim())
  const dirty = draft.toLowerCase() !== (saved || defaultHex).toLowerCase()

  // The "Use default" button is always visible so a tenant admin can
  // discover the default at any time. Disabled when the saved value is
  // already null AND the picker isn't dirty - pressing it would be a
  // no-op in that state.
  const usingDefault = !saved && draft.toLowerCase() === defaultHex.toLowerCase()

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <CardTitle>{title}</CardTitle>
            <p className="mt-1 text-sm text-muted-foreground">{description}</p>
          </div>
          {canEdit ? (
            <Button
              type="button"
              variant="ghost"
              onClick={onReset}
              disabled={busy || usingDefault}
              title={usingDefault ? 'Already using the default' : 'Reset to default'}
            >
              <RotateCcw className="size-4" /> Use default
            </Button>
          ) : null}
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <div>
          <p className="mb-2 text-xs font-medium text-muted-foreground">Presets</p>
          <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-6">
            {presets.map((p) => {
              const active = draft.toLowerCase() === p.hex.toLowerCase()
              return (
                // Swatches are inherently style-data tiles, not text buttons -
                // see the brand-preset section in BrandingPage for the full
                // justification. Native button keeps a11y wiring intact.
                // eslint-disable-next-line no-restricted-syntax
                <button
                  key={p.hex}
                  type="button"
                  onClick={() => onPick(p.hex)}
                  disabled={!canEdit}
                  title={`${p.name} (${p.hex})`}
                  aria-label={p.name}
                  aria-pressed={active}
                  className={cn(
                    'group flex flex-col items-center gap-1.5 rounded-md p-1.5 text-xs transition-colors',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                    'disabled:cursor-not-allowed disabled:opacity-60',
                    active ? 'ring-2 ring-primary ring-offset-2 ring-offset-card' : 'hover:bg-accent',
                  )}
                >
                  <span
                    className="relative flex size-10 items-center justify-center rounded-pill border border-border shadow-card"
                    style={{ backgroundColor: p.hex }}
                  >
                    {active ? <Check className="size-5 text-foreground" aria-hidden /> : null}
                  </span>
                  <span className="line-clamp-1 font-medium text-foreground">{p.name}</span>
                </button>
              )
            })}
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-[auto_1fr_auto] sm:items-end">
          <FormField
            label="Custom hex"
            hint="Click the swatch to open the OS colour picker, or type any six-character hex."
            control={
              <div className="flex items-center gap-2">
                <Input
                  type="color"
                  value={draft}
                  onChange={(e) => onHexChange(e.target.value)}
                  disabled={!canEdit}
                  aria-label="Open colour picker"
                  title="Click to open colour picker"
                  className={cn(
                    'size-9 shrink-0 cursor-pointer rounded-md p-0',
                    '[&::-webkit-color-swatch]:rounded-md [&::-webkit-color-swatch]:border-0',
                    '[&::-webkit-color-swatch-wrapper]:rounded-md [&::-webkit-color-swatch-wrapper]:p-0',
                    '[&::-moz-color-swatch]:rounded-md [&::-moz-color-swatch]:border-0',
                  )}
                />
                <Input
                  type="text"
                  value={hexInput}
                  onChange={(e) => onHexChange(e.target.value)}
                  placeholder="e.g. F0F2F6"
                  spellCheck={false}
                  className="font-mono uppercase"
                  disabled={!canEdit}
                />
              </div>
            }
          />
          <span className="hidden sm:block" />
          {canEdit ? (
            <Button
              type="button"
              onClick={onSave}
              disabled={!dirty || !isValidHex || busy}
            >
              {busy ? <Loader2 className="size-4 animate-spin" /> : <Palette className="size-4" />}
              Save
            </Button>
          ) : null}
        </div>

        {preview}

        {!canEdit ? (
          <p className="text-xs text-muted-foreground">
            You don&rsquo;t have permission to change this setting.
          </p>
        ) : null}
      </CardContent>
    </Card>
  )
}
